import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiFetch } from "../../shared/api";

/**
 * Роли, права и пользователи («Управление»). Бэкенд — `/api/access/` (app `access`).
 *
 * Ошибки валидации DRF приходят объектом `{поле: [сообщения]}`; наружу отдаём одну
 * читаемую строку (`formatApiError`), чтобы страницы не разбирали формат сами.
 */

export function formatApiError(err) {
  const data = err?.data;
  if (data && typeof data === "object") {
    const parts = Object.entries(data).map(([field, value]) => {
      const text = Array.isArray(value) ? value.join(" ") : typeof value === "object" ? JSON.stringify(value) : value;
      return field === "detail" || field === "non_field_errors" ? text : `${field}: ${text}`;
    });
    if (parts.length) return parts.join("\n");
  }
  if (err?.status === 403) return "Недостаточно прав.";
  return err?.message || "Не удалось выполнить запрос.";
}

function thunk(type, request) {
  return createAsyncThunk(type, async (arg, { rejectWithValue }) => {
    try {
      return await request(arg);
    } catch (err) {
      return rejectWithValue(formatApiError(err));
    }
  });
}

function query(params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  });
  const s = qs.toString();
  return s ? `?${s}` : "";
}

export const fetchAccessResources = thunk("access/fetchResources", () => apiFetch("/api/access/resources/"));

export const fetchRoles = thunk("access/fetchRoles", () => apiFetch("/api/access/roles/"));
export const fetchRole = thunk("access/fetchRole", (id) => apiFetch(`/api/access/roles/${id}/`));
export const createRole = thunk("access/createRole", (body) =>
  apiFetch("/api/access/roles/", { method: "POST", body }),
);
export const updateRole = thunk("access/updateRole", ({ id, ...body }) =>
  apiFetch(`/api/access/roles/${id}/`, { method: "PATCH", body }),
);
export const deleteRole = thunk("access/deleteRole", async (id) => {
  await apiFetch(`/api/access/roles/${id}/`, { method: "DELETE" });
  return id;
});

export const fetchUsers = thunk("access/fetchUsers", (params) => apiFetch(`/api/access/users/${query(params)}`));
export const fetchUser = thunk("access/fetchUser", (id) => apiFetch(`/api/access/users/${id}/`));
export const createUser = thunk("access/createUser", (body) =>
  apiFetch("/api/access/users/", { method: "POST", body }),
);
export const updateUser = thunk("access/updateUser", ({ id, ...body }) =>
  apiFetch(`/api/access/users/${id}/`, { method: "PATCH", body }),
);
/** Личные права: желаемая итоговая матрица, сервер хранит разницу с правами ролей. */
export const setUserPermissions = thunk("access/setUserPermissions", ({ id, permissions }) =>
  apiFetch(`/api/access/users/${id}/permissions/`, { method: "PUT", body: { permissions } }),
);
/** Свободный цифровой логин и цифровой пароль для нового кассира (AUFB-90). */
export const suggestCredentials = thunk("access/suggestCredentials", () =>
  apiFetch("/api/access/users/suggest-credentials/"),
);
export const setUserPassword = thunk("access/setUserPassword", ({ id, password }) =>
  apiFetch(`/api/access/users/${id}/set-password/`, { method: "POST", body: { password } }),
);

const loadable = () => ({ data: null, status: "idle", error: null });

function track(builder, action, key) {
  builder
    .addCase(action.pending, (state) => {
      state[key].status = "loading";
      state[key].error = null;
    })
    .addCase(action.fulfilled, (state, a) => {
      state[key].status = "succeeded";
      state[key].data = a.payload;
    })
    .addCase(action.rejected, (state, a) => {
      state[key].status = "failed";
      state[key].error = a.payload || a.error?.message;
    });
}

const accessSlice = createSlice({
  name: "access",
  initialState: {
    resources: loadable(),
    roles: loadable(),
    role: loadable(),
    users: loadable(),
    user: loadable(),
  },
  reducers: {},
  extraReducers: (builder) => {
    track(builder, fetchAccessResources, "resources");
    track(builder, fetchRoles, "roles");
    track(builder, fetchRole, "role");
    track(builder, fetchUsers, "users");
    track(builder, fetchUser, "user");
    builder
      .addCase(updateRole.fulfilled, (state, a) => {
        state.role.data = a.payload;
      })
      .addCase(deleteRole.fulfilled, (state, a) => {
        if (Array.isArray(state.roles.data)) state.roles.data = state.roles.data.filter((r) => r.id !== a.payload);
      })
      .addCase(updateUser.fulfilled, (state, a) => {
        state.user.data = a.payload;
      })
      .addCase(setUserPermissions.fulfilled, (state, a) => {
        state.user.data = a.payload;
      });
  },
});

export const selectAccessResources = (state) => state.access.resources;
export const selectRoles = (state) => state.access.roles;
export const selectRole = (state) => state.access.role;
export const selectUsers = (state) => state.access.users;
export const selectUser = (state) => state.access.user;

export default accessSlice.reducer;
